import React, { useEffect, useMemo, useRef } from 'react';
import { parseGoogleWebAdConfig } from '../utils/googleAds';

const STATIC_GOOGLE_AD_CLIENT =
  process.env.REACT_APP_GOOGLE_AD_CLIENT || '';
const STATIC_GOOGLE_AD_SLOT =
  process.env.REACT_APP_GOOGLE_AD_SLOT || '';
const GOOGLE_ADS_ENABLED =
  String(process.env.REACT_APP_GOOGLE_ADS_ENABLED || '').toLowerCase() === 'true';

const loadGoogleAdsScript = (client) =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    if (window.adsbygoogle) {
      resolve();
      return;
    }

    const existingScript = document.querySelector('script[data-google-adsense="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.dataset.googleAdsense = 'true';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

const GoogleAdsenseUnit = ({
  className = '',
  format = 'auto',
  layout,
  layoutKey,
  style,
  responsive = true,
  minHeight = 120,
}) => {
  const initialized = useRef(false);
  const parsedAdUnit = useMemo(
    () =>
      parseGoogleWebAdConfig({
        client: STATIC_GOOGLE_AD_CLIENT,
        slot: STATIC_GOOGLE_AD_SLOT,
      }),
    []
  );

  useEffect(() => {
    if (!GOOGLE_ADS_ENABLED || !parsedAdUnit.isEnabled || initialized.current) {
      return;
    }

    const initializeAd = () => {
      loadGoogleAdsScript(parsedAdUnit.client)
        .then(() => {
          window.adsbygoogle = window.adsbygoogle || [];
          window.adsbygoogle.push({});
          initialized.current = true;
        })
        .catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(initializeAd, { timeout: 3000 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(initializeAd, 1500);
    return () => window.clearTimeout(timeoutId);
  }, [parsedAdUnit.client, parsedAdUnit.isEnabled]);

  if (!GOOGLE_ADS_ENABLED || !parsedAdUnit.isEnabled) {
    return null;
  }

  return (
    <div
      className={className}
      style={{
        width: '100%',
        minHeight,
        overflow: 'hidden',
        ...style,
      }}
    >
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={parsedAdUnit.client}
        data-ad-slot={parsedAdUnit.slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
        {...(layout ? { 'data-ad-layout': layout } : {})}
        {...(layoutKey ? { 'data-ad-layout-key': layoutKey } : {})}
      />
    </div>
  );
};

export default GoogleAdsenseUnit;
